import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import media from '../style/media';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';

import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard, Grid, Field, FieldLabel, FieldValue } from '../style/layout';
import { Heart, Activity, Wind, Thermometer, User, ClipboardList, CheckCircle } from 'lucide-react';

const IconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: #3b82f6;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Actions = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.35);
    opacity: 0.95;
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

function formatLabel(key: string) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

const getIconForKey = (key: string) => {
  const k = key.toLowerCase();
  if (k.includes('heart') || k.includes('pulse') || k.includes('bloodpressure')) return <Heart />;
  if (k.includes('respiration') || k.includes('oxygen')) return <Wind />;
  if (k.includes('temp')) return <Thermometer />;
  if (k.includes('stress') || k.includes('index')) return <Activity />;
  if (k.includes('age') || k.includes('gender')) return <User />;
  return <ClipboardList />;
};

const getColorForKey = (key: string) => {
  const k = key.toLowerCase();
  if (k.includes('heart') || k.includes('pulse') || k.includes('bloodpressure')) return '#ef4444';
  if (k.includes('respiration') || k.includes('oxygen')) return '#06b6d4';
  if (k.includes('temp')) return '#f59e0b';
  if (k.includes('stress') || k.includes('index')) return '#8b5cf6';
  return '#3b82f6';
};

const Results: React.FC = () => {
  const location = useLocation() as any;
  const navigate = useNavigate();
  const results = location?.state?.results;

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [dismissWarning, setDismissWarning] = useState(false);
  const cameras = useCameras();

  const confidenceKeys = ['pulseRate', 'respirationRate', 'sdnn', 'meanRri', 'prq', 'rri'];
  const hasLowConfidence = !dismissWarning && results && Object.keys(results).some(key => {
    return confidenceKeys.includes(key) && results[key]?.confidenceLevel === 1;
  });

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen((prev) => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />

      <ContentWrapper>
        <GlassCard>
          <Header>
            <Title>Scan Summary</Title>
            <Subtitle>Your personalized wellness metrics overview</Subtitle>
          </Header>

          {!results ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Subtitle>No results available for this session.</Subtitle>
            </div>
          ) : (
            <>
              {hasLowConfidence && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.05)',
                  border: '1px solid rgba(239, 68, 68, 0.1)',
                  borderRadius: '24px',
                  padding: '1.5rem',
                  marginBottom: '2rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.6rem',
                    color: '#e11d48',
                    marginBottom: '0.75rem'
                  }}>
                    <Activity size={20} />
                    <span style={{ fontWeight: 800, fontSize: '1rem' }}>Accuracy Alert</span>
                  </div>
                  <p style={{
                    color: '#64748b',
                    fontSize: '0.9375rem',
                    lineHeight: '1.6',
                    margin: '0 0 1.5rem'
                  }}>
                    Some measurements show low confidence due to movement or lighting. For high accuracy, we recommend rescanning.
                  </p>
                  <Flex gap="1rem">
                    <PrimaryButton
                      onClick={() => navigate('/camera-scan')}
                      style={{
                        margin: 0,
                        background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
                        boxShadow: '0 4px 12px rgba(225, 29, 72, 0.2)'
                      }}
                    >
                      Rescan Now
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => setDismissWarning(true)}
                      style={{ margin: 0, background: 'white' }}
                    >
                      Continue
                    </SecondaryButton>
                  </Flex>
                </div>
              )}

              <Grid>
                {Object.entries(results)
                  .filter(([key]) => key.toLowerCase() !== "rri")
                  .map(([key, obj]: any) => {
                    const label = formatLabel(key);
                    const value = obj?.value;
                    const color = getColorForKey(key);

                    let display: string;
                    if (key === 'bloodPressure' && value && typeof value === 'object') {
                      display = `${value.systolic ?? '-'} / ${value.diastolic ?? '-'}`;
                    } else if (typeof value === 'object' && value !== null) {
                      display = JSON.stringify(value);
                    } else {
                      display = String(value ?? '-');
                    }

                    return (
                      <Field key={key}>
                        <IconWrapper style={{ color, background: `${color}10` }}>
                          {getIconForKey(key)}
                        </IconWrapper>
                        <FieldLabel>{label}</FieldLabel>
                        <FieldValue>{display}</FieldValue>
                      </Field>
                    );
                  })}
              </Grid>

              <Actions>
                <PrimaryButton onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
                </PrimaryButton>
              </Actions>
            </>
          )}

          {/* <Actions>
            <PrimaryButton onClick={() => navigate('/success-assessment')}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <CheckCircle size={20} />
                <span>View Full Health Report</span>
              </div>
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/patient')}>
              Start New Assessment
            </SecondaryButton>
          </Actions> */}
        </GlassCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Results;
